/* prettier-ignore-file */
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList, TextInput, ActivityIndicator, Modal } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../firebase.config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import AuthHeader from '../components/AuthHeader';
import { useGmailMessages, useSettings } from '../stores';

const GmailInbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Store info
  const [shopInfo, setShopInfo] = useState({});
  const [storeLogo, setStoreLogo] = useState("");
  const [storeNameState, setStoreNameState] = useState("");
  
  // Get settings from Zustand store
  const settings = useSettings((state) => state.settings);
  
  // Update state when settings change
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      const storeNameValue = settings.storeName?.name || "";
      const storeAddressValue = settings.startingAddress?.address || "";
      const storePhoneValue = settings.storePhone?.phone || "";
      const storeEmailValue = settings.storeEmail?.email || "";
      const storeLogoValue = settings.storeLogo?.url || "";
      
      setShopInfo({
        name: storeNameValue,
        address: storeAddressValue,
        phone: storePhoneValue,
        email: storeEmailValue,
      });
      setStoreLogo(storeLogoValue);
      setStoreNameState(storeNameValue);
    }
  }, [settings]);
  
  // Use Zustand store for messages
  const { messages: allMessages, setMessages: setAllMessages } = useGmailMessages();
  
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showComposeBox, setShowComposeBox] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('inbox'); // 'all', 'inbox', 'sent', 'drafts', 'trash'
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  
  // Get customer info from navigation state
  const customerEmail = location.state?.customerEmail;
  const customerName = location.state?.customerName;
  const storeName = location.state?.storeName || storeNameState || 'Our Store';
  const composeToFromState = location.state?.composeTo;
  const composeSubjectFromState = location.state?.composeSubject;
  
  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        // Check if user is admin
        try {
          const userDoc = await getDoc(doc(db, 'email_users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsAdmin(userData.isAdmin === true);
          }
          
          // Settings are loaded from Zustand store (no DB call needed)
        } catch (error) {
          console.error('Error loading user/settings:', error);
        }
      } else {
        navigate('/login');
      }
      setUserLoading(false);
    });
    
    return () => unsubscribe();
  }, [navigate]);
  
  // Load messages when component mounts
  useEffect(() => {
    if (!userLoading && user) {
      // If we have cached messages, show them immediately and refresh in background
      // If no cache, show loading and wait for API
      const hasCache = allMessages.length > 0;
      
      if (hasCache) {
        // Show cache immediately, refresh in background
        loadInbox(true); // Background refresh
      } else {
        // No cache, show loading and fetch
        loadInbox(true);
      }
    }
  }, [user, userLoading]);
  
  // Handle composeTo and composeSubject from navigation state - open compose box immediately
  useEffect(() => {
    if (composeToFromState) {
      setShowComposeBox(true);
      setComposeTo(composeToFromState);
      if (composeSubjectFromState) {
        setComposeSubject(composeSubjectFromState);
      }
    }
  }, [composeToFromState, composeSubjectFromState]);
  
  // Search customer threads after messages are loaded (only if not composing)
  useEffect(() => {
    if (!userLoading && user && customerEmail && allMessages.length > 0 && !composeToFromState) {
      searchCustomerThreads(customerEmail);
    }
  }, [user, userLoading, customerEmail, allMessages, composeToFromState]);
  
  const searchCustomerThreads = async (email) => {
    try {
      // First, make sure we have messages loaded
      if (allMessages.length === 0) {
        await loadInbox(false);
      }
      
      // Filter locally for customer email
      const customerMessages = allMessages.filter(msg => {
        const from = (msg.from || '').toLowerCase();
        const to = (msg.to || '').toLowerCase();
        const emailLower = email.toLowerCase();
        return from.includes(emailLower) || to.includes(emailLower);
      });
      
      if (customerMessages.length > 0) {
        // Load the most recent message automatically
        loadMessage(customerMessages[0].id);
        // Set search to show customer emails
        setSearchQuery(email);
      } else {
        // No existing thread - prepare new email
        setShowComposeBox(true);
        setComposeTo(email);
        setComposeSubject(`${storeName} Booking Info`);
        setComposeBody('');
      }
    } catch (error) {
      console.error('❌ Error searching customer threads:', error);
      // Fall back to compose
      setShowComposeBox(true);
      setComposeTo(email);
      setComposeSubject(`${storeName} Booking Info`);
    }
  };
  
  // Load all messages from Gmail API and cache in Zustand
  const loadInbox = async (forceRefresh = false) => {
    // Only fetch if we don't have messages or forcing refresh
    if (allMessages.length > 0 && !forceRefresh) {
      return;
    }
    
    try {
      // Only show loading spinner if we don't have cached messages
      if (allMessages.length === 0) {
        setLoading(true);
      }
      
      const listGmailMessages = httpsCallable(functions, 'listGmailMessages');
      const result = await listGmailMessages({ 
        maxResults: 100, // Load more messages to cache
        query: 'in:inbox OR in:sent OR in:trash OR in:drafts'
      });
      
      if (result.data.messages) {
        // Store all messages in Zustand
        setAllMessages(result.data.messages);
      } else {
        setAllMessages([]);
      }
    } catch (error) {
      console.error('❌ Error loading inbox:', error);
      alert('Failed to load inbox: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const loadMessage = async (messageId) => {
    try {
      setLoadingMessage(true);
      setShowReplyBox(false);
      setReplyText('');
      
      const getGmailMessage = httpsCallable(functions, 'getGmailMessage');
      const result = await getGmailMessage({ messageId });
      
      setSelectedMessage(result.data);
      
      // Mark as read
      if (result.data.unread) {
        markAsRead(messageId);
      }
    } catch (error) {
      console.error('❌ Error loading message:', error);
      alert('Failed to load message: ' + error.message);
    } finally {
      setLoadingMessage(false);
    }
  };
  
  const markAsRead = async (messageId) => {
    try {
      // Update Zustand store immediately (optimistic update)
      const { updateMessage } = useGmailMessages.getState();
      updateMessage(messageId, { unread: false });
      
      // Update selected message if needed
      if (selectedMessage && selectedMessage.id === messageId) {
        setSelectedMessage({ ...selectedMessage, unread: false });
      }
      
      // Call API to update Gmail
      const markGmailAsRead = httpsCallable(functions, 'markGmailAsRead');
      await markGmailAsRead({ messageId });
    } catch (error) {
      console.error('❌ Error marking as read:', error);
      // Revert optimistic update on error
      const { updateMessage } = useGmailMessages.getState();
      updateMessage(messageId, { unread: true });
    }
  };
  
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Move this message to trash?')) return;
    
    try {
      // Update Zustand store immediately
      const { updateMessage } = useGmailMessages.getState();
      const message = allMessages.find(m => m.id === messageId);
      if (message) {
        const updatedLabels = [...(message.labels || [])];
        if (!updatedLabels.includes('TRASH')) {
          updatedLabels.push('TRASH');
        }
        // Remove from inbox if present
        const inboxIndex = updatedLabels.indexOf('INBOX');
        if (inboxIndex > -1) {
          updatedLabels.splice(inboxIndex, 1);
        }
        updateMessage(messageId, { labels: updatedLabels });
      }
      
      // Clear selected message
      setSelectedMessage(null);
      
      // Call API to update Gmail
      const deleteGmailMessage = httpsCallable(functions, 'deleteGmailMessage');
      await deleteGmailMessage({ messageId });
      
      alert('Message moved to trash');
    } catch (error) {
      console.error('❌ Error deleting message:', error);
      alert('Failed to delete message: ' + error.message);
    }
  };
  
  const archiveMessage = async (messageId) => {
    try {
      // Update Zustand store immediately
      const { updateMessage } = useGmailMessages.getState();
      const message = allMessages.find(m => m.id === messageId);
      if (message) {
        const updatedLabels = (message.labels || []).filter(label => label !== 'INBOX');
        updateMessage(messageId, { labels: updatedLabels });
      }
      
      // Clear selected message
      setSelectedMessage(null);
      
      // Call API to update Gmail
      const archiveGmailMessage = httpsCallable(functions, 'archiveGmailMessage');
      await archiveGmailMessage({ messageId });
      
      alert('Message archived');
    } catch (error) {
      console.error('❌ Error archiving message:', error);
      alert('Failed to archive message: ' + error.message);
    }
  };
  
  const saveDraft = async () => {
    // Don't save if all fields are empty
    if (!composeTo.trim() && !composeSubject.trim() && !composeBody.trim()) {
      return;
    }
    
    try {
      setSavingDraft(true);
      
      const saveGmailDraft = httpsCallable(functions, 'saveGmailDraft');
      const result = await saveGmailDraft({
        to: composeTo,
        subject: composeSubject,
        body: composeBody,
        draftId: draftId,
      });
      
      // Store draft ID for future updates
      if (result.data.draftId) {
        setDraftId(result.data.draftId);
      }
    } catch (error) {
      console.error('❌ Error saving draft:', error);
    } finally {
      setSavingDraft(false);
    }
  };
  
  // Auto-save draft with debounce
  useEffect(() => {
    if (showComposeBox && (composeTo || composeSubject || composeBody)) {
      const timer = setTimeout(() => {
        saveDraft();
      }, 3000); // Auto-save after 3 seconds of inactivity
      
      return () => clearTimeout(timer);
    }
  }, [composeTo, composeSubject, composeBody, showComposeBox]);
  
  const sendReply = async () => {
    if (!replyText.trim() || !replyTo.trim() || !selectedMessage) return;
    
    try {
      setSending(true);
      
      // Prepare subject (add Re: if not present)
      const replySubject = selectedMessage.subject.startsWith('Re:') 
        ? selectedMessage.subject 
        : `Re: ${selectedMessage.subject}`;
      
      const sendGmailReply = httpsCallable(functions, 'sendGmailReply');
      await sendGmailReply({
        threadId: selectedMessage.threadId,
        to: replyTo,
        subject: replySubject,
        body: replyText,
        inReplyTo: selectedMessage.messageId,
      });
      
      alert('✅ Reply sent successfully!');
      setReplyText('');
      setReplyTo('');
      setShowReplyBox(false);
      
      // Reload inbox to show updated thread
      loadInbox(true);
    } catch (error) {
      console.error('❌ Error sending reply:', error);
      alert('Failed to send reply: ' + error.message);
    } finally {
      setSending(false);
    }
  };
  
  const sendNewEmail = async () => {
    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim()) {
      alert('Please fill in To, Subject, and Message fields');
      return;
    }
    
    try {
      setSending(true);
      
      const sendGmailReply = httpsCallable(functions, 'sendGmailReply');
      await sendGmailReply({
        to: composeTo,
        subject: composeSubject,
        body: composeBody,
      });
      
      alert('✅ Email sent successfully!');
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      setDraftId(null);
      setShowComposeBox(false);
      
      // Reload inbox from API to show sent message
      loadInbox(true);
    } catch (error) {
      console.error('❌ Error sending email:', error);
      alert('Failed to send email: ' + error.message);
    } finally {
      setSending(false);
    }
  };
  
  const performSearch = (query) => {
    if (!query || query.trim().length < 3) {
      loadInbox('');
      return;
    }
    
    // Build Gmail search query for email, name, or phone
    // Search in subject and body for name/email/phone
    const searchTerm = query.trim();
    const gmailQuery = `{from:${searchTerm} OR to:${searchTerm} OR subject:${searchTerm} OR ${searchTerm}}`;
    
    loadInbox(gmailQuery);
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    // The filtered results will automatically update through useMemo
  };
  
  // Get icon for message based on labels
  const getMessageIcon = (labels) => {
    if (!labels) return '📧';
    if (labels.includes('SENT')) return '📤';
    if (labels.includes('DRAFT')) return '📝';
    if (labels.includes('TRASH')) return '🗑️';
    if (labels.includes('INBOX')) return '📥';
    return '📧';
  };
  
  // Filter messages by folder and search query (client-side)
  const filteredMessages = useMemo(() => {
    let filtered = allMessages;
    
    // Apply search filter
    if (searchQuery && searchQuery.length >= 3) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(msg => {
        const from = (msg.from || '').toLowerCase();
        const to = (msg.to || '').toLowerCase();
        const subject = (msg.subject || '').toLowerCase();
        const snippet = (msg.snippet || '').toLowerCase();
        return from.includes(query) || to.includes(query) || subject.includes(query) || snippet.includes(query);
      });
    }
    
    // Apply folder filter (only if no search query)
    if (!searchQuery || searchQuery.length < 3) {
      switch (selectedFolder) {
        case 'inbox':
          filtered = filtered.filter(msg => msg.labels?.includes('INBOX'));
          break;
        case 'sent':
          filtered = filtered.filter(msg => msg.labels?.includes('SENT'));
          break;
        case 'drafts':
          filtered = filtered.filter(msg => msg.labels?.includes('DRAFT'));
          break;
        case 'trash':
          filtered = filtered.filter(msg => msg.labels?.includes('TRASH'));
          break;
        case 'all':
        default:
          // Show all messages
          break;
      }
    }
    
    return filtered;
  }, [allMessages, selectedFolder, searchQuery]);
  
  // Update unread count
  useEffect(() => {
    const unread = filteredMessages.filter(m => m.unread).length;
    setUnreadCount(unread);
  }, [filteredMessages]);
  
  // Handle folder change (instant, no API call)
  const handleFolderChange = (folder) => {
    setSelectedFolder(folder);
    setSearchQuery('');
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffHours = (now - date) / (1000 * 60 * 60);
      
      if (diffHours < 24) {
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else if (diffHours < 168) {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch {
      return dateString;
    }
  };
  
  const extractSenderName = (from) => {
    const nameMatch = from.match(/^(.+?)\s*</);
    if (nameMatch) {
      return nameMatch[1].replace(/"/g, '');
    }
    return from.split('@')[0];
  };
  
  if (userLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }
  
  if (!user) {
    return null; // Will redirect to login
  }
  
  return (
    <View style={[styles.container, { height: window.innerHeight }]}>
      <AuthHeader 
        title="📧 Gmail Inbox"
        subtitle={`${unreadCount} unread`}
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={true}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />
      
      <View style={styles.content}>
        {/* Toolbar */}
        <View style={styles.toolbar}>
          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              placeholder="Search emails (name, email, phone)..."
              placeholderTextColor="#AEB8B6"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            <TouchableOpacity dataSet={{ hover: "lift" }} 
              style={[styles.clearButton, searchQuery.length === 0 && styles.clearButtonDisabled]} 
              onPress={clearSearch}
              disabled={searchQuery.length === 0}
            >
              <Text style={[styles.clearButtonText, searchQuery.length === 0 && styles.clearButtonTextDisabled]}>✕</Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }} 
              style={styles.composeIconButton} 
              onPress={() => {
                setShowComposeBox(true);
                setSelectedMessage(null);
                setComposeTo('');
                setComposeSubject('');
                setComposeBody('');
              }}
            >
              <Text style={styles.composeIconText}>✏️</Text>
            </TouchableOpacity>
          </View>
          
          {/* Folder Tabs */}
          <View style={styles.folderTabs}>
            <TouchableOpacity 
              style={[styles.folderTab, selectedFolder === 'all' && styles.folderTabActive]}
              onPress={() => handleFolderChange('all')}
            >
              <Text style={[styles.folderTabText, selectedFolder === 'all' && styles.folderTabTextActive]}>
                📬 All
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.folderTab, selectedFolder === 'inbox' && styles.folderTabActive]}
              onPress={() => handleFolderChange('inbox')}
            >
              <Text style={[styles.folderTabText, selectedFolder === 'inbox' && styles.folderTabTextActive]}>
                📥 Inbox
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.folderTab, selectedFolder === 'sent' && styles.folderTabActive]}
              onPress={() => handleFolderChange('sent')}
            >
              <Text style={[styles.folderTabText, selectedFolder === 'sent' && styles.folderTabTextActive]}>
                📤 Sent
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.folderTab, selectedFolder === 'drafts' && styles.folderTabActive]}
              onPress={() => handleFolderChange('drafts')}
            >
              <Text style={[styles.folderTabText, selectedFolder === 'drafts' && styles.folderTabTextActive]}>
                📝 Drafts
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.folderTab, selectedFolder === 'trash' && styles.folderTabActive]}
              onPress={() => handleFolderChange('trash')}
            >
              <Text style={[styles.folderTabText, selectedFolder === 'trash' && styles.folderTabTextActive]}>
                🗑️ Trash
              </Text>
            </TouchableOpacity>
          </View>
          
        </View>
        
        <View style={styles.mainContent}>
          {/* Message List */}
          <View style={styles.messageList}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#0E7C6B" />
                <Text style={styles.loadingText}>Loading inbox...</Text>
              </View>
            ) : filteredMessages.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  {selectedFolder === 'inbox' ? '📭 No current threads' : '📭 No messages found'}
                </Text>
              </View>
            ) : (
              <FlatList
                style={{ flex: 1 }}
                data={filteredMessages}
                keyExtractor={(item) => item.id}
                renderItem={({ item: msg }) => (
                  <TouchableOpacity
                    style={[
                      styles.messageItem,
                      msg.unread && styles.messageItemUnread,
                      selectedMessage?.id === msg.id && styles.messageItemSelected,
                    ]}
                    onPress={() => loadMessage(msg.id)}
                  >
                    <View style={styles.messageItemHeader}>
                      <View style={styles.messageItemSenderRow}>
                        <Text style={styles.messageFolderIcon}>{getMessageIcon(msg.labels)}</Text>
                        <Text style={[styles.messageSender, msg.unread && styles.messageSenderUnread]}>
                          {extractSenderName(msg.from)}
                        </Text>
                      </View>
                      <Text style={styles.messageDate}>{formatDate(msg.date)}</Text>
                    </View>
                    <Text style={[styles.messageSubject, msg.unread && styles.messageSubjectUnread]} numberOfLines={1}>
                      {msg.subject || '(No Subject)'}
                    </Text>
                    <Text style={styles.messageSnippet} numberOfLines={2}>
                      {msg.snippet}
                    </Text>
                    {msg.unread && <View style={styles.unreadDot} />}
                  </TouchableOpacity>
                )}
              />
            )}
          </View>
          
          {/* Message Detail */}
          <View style={styles.messageDetail}>
            {showComposeBox && !selectedMessage ? (
              <ScrollView style={styles.messageDetailScroll}>
                <View style={styles.composeContainer}>
                  <View style={styles.composeTitleRow}>
                    <Text style={styles.composeTitle}>✉️ New Email</Text>
                    {savingDraft && (
                      <Text style={styles.draftSavingText}>💾 Saving draft...</Text>
                    )}
                  </View>
                  
                  <View style={styles.composeField}>
                    <Text style={styles.composeLabel}>To:</Text>
                    <TextInput
                      style={styles.composeInput}
                      value={composeTo}
                      onChangeText={setComposeTo}
                      placeholder="recipient@example.com"
                      placeholderTextColor="#AEB8B6"
                    />
                  </View>
                  
                  <View style={styles.composeField}>
                    <Text style={styles.composeLabel}>Subject:</Text>
                    <TextInput
                      style={styles.composeInput}
                      value={composeSubject}
                      onChangeText={setComposeSubject}
                      placeholder="Subject line"
                      placeholderTextColor="#AEB8B6"
                    />
                  </View>
                  
                  <View style={styles.composeField}>
                    <Text style={styles.composeLabel}>Message:</Text>
                    <TextInput
                      style={styles.composeBodyInput}
                      value={composeBody}
                      onChangeText={setComposeBody}
                      placeholder="Type your message..."
                      placeholderTextColor="#AEB8B6"
                      multiline
                      numberOfLines={12}
                    />
                  </View>
                  
                  <View style={styles.replyButtons}>
                    <TouchableOpacity dataSet={{ hover: "lift" }} 
                      style={styles.sendButton}
                      onPress={sendNewEmail}
                      disabled={sending || !composeTo.trim() || !composeSubject.trim() || !composeBody.trim()}
                    >
                      <Text style={styles.sendButtonText}>
                        {sending ? 'Sending...' : '✉️ Send Email'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }} 
                      style={styles.cancelButton}
                      onPress={() => {
                        setShowComposeBox(false);
                        setComposeTo('');
                        setComposeSubject('');
                        setComposeBody('');
                      }}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </ScrollView>
            ) : loadingMessage ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#0E7C6B" />
                <Text style={styles.loadingText}>Loading message...</Text>
              </View>
            ) : selectedMessage ? (
              <>
                {/* Action Buttons - Fixed at Top */}
                <View style={styles.messageActions}>
                  {!showReplyBox ? (
                    <TouchableOpacity dataSet={{ hover: "lift" }} 
                      style={styles.replyButton}
                      onPress={() => {
                        // Extract sender email and set it for reply
                        const fromEmail = selectedMessage.from.match(/<(.+?)>/)?.[1] || selectedMessage.from;
                        setReplyTo(fromEmail);
                        setShowReplyBox(true);
                      }}
                    >
                      <Text style={styles.replyButtonText}>↩️ Reply</Text>
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity dataSet={{ hover: "lift" }} 
                    style={styles.archiveButton}
                    onPress={() => archiveMessage(selectedMessage.id)}
                  >
                    <Text style={styles.archiveButtonText}>📦 Archive</Text>
                  </TouchableOpacity>
                  <TouchableOpacity dataSet={{ hover: "lift" }} 
                    style={styles.deleteButton}
                    onPress={() => deleteMessage(selectedMessage.id)}
                  >
                    <Text style={styles.deleteButtonText}>🗑️ Delete</Text>
                  </TouchableOpacity>
                </View>
                
                {/* Reply Box - Fixed Below Action Buttons */}
                {showReplyBox && (
                  <View style={styles.replyBox}>
                    <View style={styles.composeField}>
                      <Text style={styles.composeLabel}>To:</Text>
                      <TextInput
                        style={styles.composeInput}
                        value={replyTo}
                        onChangeText={setReplyTo}
                        placeholder="recipient@example.com"
                        placeholderTextColor="#AEB8B6"
                      />
                    </View>
                    <TextInput
                      style={styles.replyInput}
                      placeholder="Type your reply..."
                      placeholderTextColor="#AEB8B6"
                      value={replyText}
                      onChangeText={setReplyText}
                      multiline
                      numberOfLines={6}
                    />
                    <View style={styles.replyButtons}>
                      <TouchableOpacity dataSet={{ hover: "lift" }} 
                        style={styles.sendButton}
                        onPress={sendReply}
                        disabled={sending || !replyText.trim() || !replyTo.trim()}
                      >
                        <Text style={styles.sendButtonText}>
                          {sending ? 'Sending...' : '✉️ Send Reply'}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity dataSet={{ hover: "lift" }} 
                        style={styles.cancelButton}
                        onPress={() => {
                          setShowReplyBox(false);
                          setReplyText('');
                          setReplyTo('');
                        }}
                      >
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
                
                {/* Scrollable Content */}
                <ScrollView style={styles.messageDetailScroll}>
                  <View style={styles.messageHeader}>
                    <Text style={styles.messageHeaderSubject}>{selectedMessage.subject}</Text>
                    <View style={styles.messageHeaderRow}>
                      <Text style={styles.messageHeaderLabel}>From:</Text>
                      <Text style={styles.messageHeaderValue}>{selectedMessage.from}</Text>
                    </View>
                    <View style={styles.messageHeaderRow}>
                      <Text style={styles.messageHeaderLabel}>To:</Text>
                      <Text style={styles.messageHeaderValue}>{selectedMessage.to}</Text>
                    </View>
                    {selectedMessage.cc ? (
                      <View style={styles.messageHeaderRow}>
                        <Text style={styles.messageHeaderLabel}>Cc:</Text>
                        <Text style={styles.messageHeaderValue}>{selectedMessage.cc}</Text>
                      </View>
                    ) : null}
                    <View style={styles.messageHeaderRow}>
                      <Text style={styles.messageHeaderLabel}>Date:</Text>
                      <Text style={styles.messageHeaderValue}>
                        {new Date(selectedMessage.date).toLocaleString()}
                      </Text>
                    </View>
                  </View>
                  
                  {/* Email Body */}
                  <View style={styles.messageBody}>
                    <div 
                      dangerouslySetInnerHTML={{ __html: selectedMessage.body }} 
                      style={{ fontSize: 14, color: '#2E3A38', lineHeight: '1.6' }}
                    />
                  </View>
                </ScrollView>
              </>
            ) : (
              <View style={styles.noSelectionContainer}>
                <Text style={styles.noSelectionText}>📧</Text>
                <Text style={styles.noSelectionSubtext}>Select a message to read</Text>
              </View>
            )}
          </View>
        </View>

        {/* Blocking Modal for Sending Email */}
        <Modal
          visible={sending}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Sending email...</Text>
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#F6F8F7',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  toolbar: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#DDE3E2',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
  },
  clearButton: {
    backgroundColor: '#C4453D',
    borderRadius: 6,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  clearButtonDisabled: {
    backgroundColor: '#DDE3E2',
    opacity: 0.5,
  },
  clearButtonText: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 'bold',
  },
  clearButtonTextDisabled: {
    color: '#8A9794',
  },
  composeIconButton: {
    backgroundColor: '#1F8A5B',
    borderRadius: 6,
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  composeIconText: {
    fontSize: 20,
  },
  folderTabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#DDE3E2',
    backgroundColor: '#F4F6F6',
    marginBottom: 12,
  },
  folderTab: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 4,
    alignItems: 'center',
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  folderTabActive: {
    borderBottomColor: '#0E7C6B',
    backgroundColor: '#FDF3E3',
  },
  folderTabText: {
    fontSize: 12,
    color: '#5C6B68',
    fontWeight: '500',
  },
  folderTabTextActive: {
    color: '#0E7C6B',
    fontWeight: '700',
  },
  toolbarActions: {
    flexDirection: 'row',
    gap: 8,
  },
  toolbarButton: {
    flex: 1,
    backgroundColor: '#1F8A5B',
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  toolbarButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  messageList: {
    width: '40%',
    borderRightWidth: 1,
    borderRightColor: '#DDE3E2',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  messageItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#EDF1F0',
    backgroundColor: '#FFFFFF',
    position: 'relative',
  },
  messageItemUnread: {
    backgroundColor: '#E3F2EF',
  },
  messageItemSelected: {
    backgroundColor: '#E3F2EF',
    borderLeftWidth: 4,
    borderLeftColor: '#0E7C6B',
  },
  messageItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  messageItemSenderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  messageFolderIcon: {
    fontSize: 14,
    marginRight: 6,
  },
  messageSender: {
    fontSize: 14,
    color: '#2E3A38',
    fontWeight: '500',
    flex: 1,
  },
  messageSenderUnread: {
    fontWeight: '700',
  },
  messageDate: {
    fontSize: 12,
    color: '#5C6B68',
  },
  messageSubject: {
    fontSize: 13,
    color: '#5C6B68',
    marginBottom: 4,
  },
  messageSubjectUnread: {
    color: '#0F1A19',
    fontWeight: '600',
  },
  messageSnippet: {
    fontSize: 12,
    color: '#8A9794',
    lineHeight: 16,
  },
  unreadDot: {
    position: 'absolute',
    top: 20,
    right: 16,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#0E7C6B',
  },
  messageDetail: {
    width: '60%',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  messageDetailScroll: {
    flex: 1,
  },
  messageHeader: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#DDE3E2',
    backgroundColor: '#F4F6F6',
  },
  messageHeaderSubject: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 16,
  },
  messageHeaderRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  messageHeaderLabel: {
    fontSize: 13,
    color: '#5C6B68',
    fontWeight: '600',
    width: 60,
  },
  messageHeaderValue: {
    fontSize: 13,
    color: '#2E3A38',
    flex: 1,
  },
  messageBody: {
    padding: 20,
    backgroundColor: '#FFFFFF',
  },
  messageActions: {
    flexDirection: 'row',
    padding: 12,
    gap: 8,
    flexWrap: 'wrap',
    backgroundColor: '#F4F6F6',
    borderBottomWidth: 1,
    borderBottomColor: '#DDE3E2',
  },
  replyButton: {
    flex: 1,
    minWidth: 90,
    backgroundColor: '#0E7C6B',
    borderRadius: 5,
    padding: 10,
    alignItems: 'center',
  },
  replyButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  archiveButton: {
    flex: 1,
    minWidth: 90,
    backgroundColor: '#1F8A5B',
    borderRadius: 5,
    padding: 10,
    alignItems: 'center',
  },
  archiveButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    flex: 1,
    minWidth: 90,
    backgroundColor: '#C4453D',
    borderRadius: 5,
    padding: 10,
    alignItems: 'center',
  },
  deleteButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  replyBox: {
    margin: 20,
    padding: 16,
    backgroundColor: '#F4F6F6',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#DDE3E2',
  },
  replyInput: {
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#FFFFFF',
    minHeight: 120,
    textAlignVertical: 'top',
    marginBottom: 12,
  },
  replyButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  sendButton: {
    flex: 1,
    backgroundColor: '#1F8A5B',
    borderRadius: 6,
    padding: 12,
    alignItems: 'center',
  },
  sendButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#5C6B68',
    borderRadius: 6,
    padding: 12,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#5C6B68',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#5C6B68',
  },
  noSelectionContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noSelectionText: {
    fontSize: 60,
    marginBottom: 16,
  },
  noSelectionSubtext: {
    fontSize: 16,
    color: '#5C6B68',
  },
  unauthorizedContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  unauthorizedIcon: {
    fontSize: 80,
    marginBottom: 20,
  },
  unauthorizedText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 12,
  },
  unauthorizedSubtext: {
    fontSize: 16,
    color: '#5C6B68',
    textAlign: 'center',
    marginBottom: 30,
  },
  backButton: {
    backgroundColor: '#0E7C6B',
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  composeContainer: {
    padding: 20,
  },
  composeTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  composeTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2E3A38',
  },
  draftSavingText: {
    fontSize: 12,
    color: '#1F8A5B',
    fontStyle: 'italic',
  },
  composeField: {
    marginBottom: 16,
  },
  composeLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E3A38',
    marginBottom: 8,
  },
  composeInput: {
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#FFFFFF',
  },
  composeBodyInput: {
    borderWidth: 1,
    borderColor: '#DDE3E2',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#FFFFFF',
    minHeight: 200,
    textAlignVertical: 'top',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    padding: 30,
    borderRadius: 8,
    alignItems: 'center',
    minWidth: 200,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  modalText: {
    marginTop: 16,
    fontSize: 16,
    color: '#2E3A38',
    fontWeight: '600',
  },
});

export default GmailInbox;

